import { FormHelperText, InputLabel } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/styles";
import { DataGrid } from '@mui/x-data-grid';
import { useFormikContext } from "formik";
import sum from "lodash/sum";
import React, { useMemo } from "react";
import { Row, Col } from "react-grid-system";

import SectionHeader from "../../../../../employer/JobCasts/view/Candidates/actions/modals/Hire/SectionHeader";
import FormContextNumberInput from "../../../../../forms/formik/FormContextInputs/FormContextNumberInput";
import FormContextTextField from "../../../../../forms/formik/FormContextInputs/FormContextTextField";

import { customSubTextFieldProps, formatter } from "../utils";

import TimesheetHoursTable from "./TimesheetHoursTable";
import { buildTimesheetColumns } from "../../../../../../util/workWeek";
import { computeTotalsFromAllocations } from "./utils";

const useStyles = makeStyles((theme) => ({
  additionalBilling: {
    "& .MuiInputBase-root:not(.Mui-focused)": {
      color: "inherit",
    },
  },
  totalsGrid: {
    color: theme.palette.type === 'light' ? 'rgba(0,0,0,.85)' : 'rgba(255,255,255,0.85)',
    letterSpacing: 'normal',
    '& .MuiDataGrid-columnsContainer': {
      backgroundColor: theme.palette.type === 'light' ? '#EDEDED' : '#1d1d1d',
    },
    '& .MuiDataGrid-iconSeparator': {
      display: 'none',
    },
    '& .MuiDataGrid-columnHeader:first-of-type .MuiDataGrid-columnHeaderTitle': {
      color: 'transparent',
      userSelect: 'none',
    },
    '& .MuiDataGrid-columnHeader:last-of-type .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 700,
    },
    '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell': {
      borderRight: `1px solid ${
        theme.palette.type === 'light' ? '#f0f0f0' : '#303030'
      }`,
    },
    '& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell': {
      borderBottom: `1px solid ${
        theme.palette.type === 'light' ? '#f0f0f0' : '#303030'
      }`,
    },
    '& .MuiDataGrid-row.Mui-selected, .MuiDataGrid-row.Mui-selected:hover': {
      backgroundColor: '#fff',
    },
    '& .MuiDataGrid-row .MuiDataGrid-cell:first-of-type, .MuiDataGrid-row .MuiDataGrid-cell:last-of-type': {
      fontWeight: 700,
    },
    '& .MuiDataGrid-row .MuiDataGrid-cell:last-of-type': {
      backgroundColor: theme.palette.type === 'light' ? '#EDEDED' : '#1d1d1d',
    },
    '& .MuiDataGrid-cell': {
      color: theme.palette.type === 'light' ? 'rgba(0,0,0,.85)' : 'rgba(255,255,255,0.65)',
    },
  },
}));

const disabledProps = (disabled) =>
  disabled
    ? {
        InputProps: {
          style: { color: "#90A4AE !important" },
        },
        InputLabelProps: { className: "disabled-input-label", shrink: true },
      }
    : {};

export default function ProjectAllocationForm({
  contractAllocations,
  disabled,
  hourlyBillRate,
  tempRfiTakeRate,
  payPeriod,
  edit,
  initialWeekday,
}) {
  const classes = useStyles();
  const { values, errors, submitCount } = useFormikContext();

  const totalsColumns = useMemo(
    () => buildTimesheetColumns(initialWeekday).map((col) => ({ ...col, editable: false })),
    [initialWeekday]
  );

  const getAllocationLabel = (code) => {
    if (!contractAllocations) return code;
    const alloc = contractAllocations.find(a => a.code === code);
    if (!alloc) return code;
    return alloc.type === "gl_string" ? `GL String: ${code}` : `Project: ${code}`;
  };

  const { totalRows, totalAdditionalBilling, totalBill, totalEarn } = useMemo(() => {
    const { totalRows, totalAdditionalBilling } = computeTotalsFromAllocations(
      values.allocations
    );

    const rateHours = sum([
      totalRows.find(r => r.id === "Standard")?.total ?? 0,
      (totalRows.find(r => r.id === "Overtime")?.total ?? 0) * 1.5,
      (totalRows.find(r => r.id === "Double Time")?.total ?? 0) * 2,
    ]);

    const totalBill = rateHours * hourlyBillRate + totalAdditionalBilling;
    const totalEarn = totalBill * (1.0 - tempRfiTakeRate);

    return { totalRows, totalAdditionalBilling, totalBill, totalEarn };
  }, [values.allocations, hourlyBillRate, tempRfiTakeRate]);

  const allocationError = submitCount > 0 && typeof errors.allocations === "string"
    ? errors.allocations
    : null;

  return (
    <>
      {(values.allocations || []).map((item, idx) => (
        <div key={item.code} style={{ marginBottom: 24 }}>
          <SectionHeader title={getAllocationLabel(item.code)} />
          <TimesheetHoursTable
            disabled={disabled}
            fieldName={`allocations[${idx}].timesheetHours`}
            hideQuickFill
            initialWeekday={initialWeekday}
          />
          <Row style={{ padding: "12px 0" }}>
            <Col md={4}>
              <FormContextNumberInput
                disabled={disabled}
                decimalScale={2}
                label="Additional Billing"
                id={`allocations[${idx}].additionalBilling`}
                className={classes.additionalBilling}
                InputProps={{
                  startAdornment: "$",
                  inputProps: { max: 5000 },
                }}
              />
            </Col>
            <Col>
              <FormContextTextField
                disabled={disabled}
                id={`allocations[${idx}].additionalBillingReason`}
                label="Additional Billing Explanation"
                {...disabledProps(disabled)}
                inputProps={{
                  maxLength: 1000,
                }}
              />
            </Col>
          </Row>
        </div>
      ))}

      {allocationError && (
        <FormHelperText
          style={{ color: "#FF6D6D", fontSize: "11px", marginBottom: 8 }}
        >
          {allocationError}
        </FormHelperText>
      )}

      <SectionHeader title="Overall Totals" />
      <InputLabel component="legend" style={{ marginBottom: "16px" }}>
        Combined Hours
      </InputLabel>
      <DataGrid
        className={classes.totalsGrid}
        rows={totalRows}
        columns={totalsColumns}
        hideFooter
        disableColumnFilter
        disableColumnMenu
        autoHeight
        isCellEditable={() => false}
      />
      <Row style={{ padding: "12px 0" }}>
        <Col md={4}>
          <TextField
            {...customSubTextFieldProps}
            id="total-additional-billing"
            label="Total Additional Billing"
            value={formatter.format(totalAdditionalBilling)}
          />
        </Col>
        <Col md={4}>
          <TextField
            {...customSubTextFieldProps}
            id="total-bill"
            label="Total Bill"
            value={formatter.format(
              !edit && (payPeriod.employerCollectionAmount || 0) > 0
                ? payPeriod.employerCollectionAmount
                : totalBill
            )}
          />
        </Col>
        <Col md={4}>
          <TextField
            {...customSubTextFieldProps}
            id="total-earn"
            label="Total Earn"
            value={formatter.format(
              !edit && (payPeriod.recruiterPayoutAmount || 0) > 0
                ? payPeriod.recruiterPayoutAmount
                : totalEarn
            )}
          />
        </Col>
      </Row>
    </>
  );
}
